import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useLocalStorage, writeStorage } from "@rehooks/local-storage";
import Footer from "components/layout/Footer";
import AccessibleIconButton from "components/base/AccessibleIconButton";
import RightDrawer from "components/base/RightDrawer";
import AccessSidebar from "features/AccessSidebar";
import ColumnsSection from "features/Collections/components/ColumnSection";
import GroupAvatar from "features/Collections/components/GroupAvatar";
import ProfileAvatar from "components/base/ProfileAvatar";
import SnapshotBookmarkSidebar from "./SnapshotBookmarkSidebar";
import CollectionTableComponent from "features/SourceSearch/components/collection-table-component";
import { useColumns } from "features/SourceSearch/components/generate-columns";
import WorkCitedTable from "features/WorkCited/components/Table";
import CollectionNotes from "features/Collections/components/CollectionNotes";
import { fillParent, scrollingBox } from "utils/base-styles";
import { getRegularDate } from "utils/time.util";
import { getArchiveSnapshotDetail, getArchiveWorkCitedDocument } from "../services";
import {
  mapSnapshotSources,
  mapSnapshotWorkCited,
  submissionFolderPath,
} from "../utils";

const TABS = [
  { key: "bookmarks", label: "Bookmarks" },
  { key: "workcited", label: "Works Cited" },
  { key: "collectionnotes", label: "Collection Notes" },
  { key: "teachernotes", label: "Teacher's Notes" },
];

// Same columns as the live single-collection grid (minus cross-collection membership,
// which doesn't apply to a single archived collection). score/mediaBiasRating/
// charityNavigatorSource are populated once GetArchiveSnapshotDetail returns them.
const SNAPSHOT_COLUMNS = [
  "createdDateTime_dtm",
  "title_chr",
  "sourceName_chr",
  "sourcePublishedDateTime_dtm",
  "score",
  "mediaBiasRating",
  "charityNavigatorSource",
  "notesCount_cnt",
  "isCitationAvailable_ysn",
  "isReadingLevelAvailable_ysn",
];

const SNAPSHOT_VISIBILITY_MODEL = {
  createdDateTime_dtm: false,
  title_chr: true,
  sourceName_chr: true,
  sourcePublishedDateTime_dtm: true,
  score: false,
  mediaBiasRating: false,
  charityNavigatorSource: false,
  notesCount_cnt: true,
  isCitationAvailable_ysn: true,
  isReadingLevelAvailable_ysn: false,
};

/** One summary tile in the row above the tabs. */
function StatTile({ label, value, loading, children }) {
  const theme = useTheme();
  return (
    <Stack
      gap={1}
      alignItems="center"
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        py: 2.5,
        borderRadius: 1,
        backgroundColor: theme.palette.secondary.main,
      }}
    >
      <Typography
        variant="body2"
        color={theme.palette.secondary.contrastText}
        noWrap
        sx={{ maxWidth: "100%" }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width={90} height={24} />
      ) : children ? (
        children
      ) : (
        // Long values (emails, distribution list names) wrap instead of spilling out
        // of the tile, which a centred noWrap value does on both sides.
        <Typography
          variant="body1"
          align="center"
          sx={{ maxWidth: "100%", overflowWrap: "anywhere" }}
        >
          {value}
        </Typography>
      )}
    </Stack>
  );
}

/** Owner tile content: creator's avatar/email, plus a stacked group avatar and "+N
 * others" once the frozen share list carries more than one person — matches the
 * Owner column on the submission folder list. */
function SnapshotOwners({ owners }) {
  if (!owners.length) {
    return (
      <Typography variant="body1" align="center" sx={{ maxWidth: "100%", overflowWrap: "anywhere" }}>
        —
      </Typography>
    );
  }
  const primary = owners[0];
  const additionalOwnerCount = owners.length - 1;
  return (
    <Stack direction="row" gap={1.5} alignItems="center" sx={{ minWidth: 0 }}>
      {owners.length > 1 ? (
        <GroupAvatar
          userIds={owners.map((owner, index) => owner.userId || `snapshot-owner-${index}`)}
          userAvatars={owners.map((owner) => owner.avatarUrl)}
          max={2}
        />
      ) : (
        <ProfileAvatar userId={primary.userId} avatarUrl={primary.avatarUrl} />
      )}
      <Typography variant="body1" sx={{ overflowWrap: "anywhere" }}>
        {primary.email}
        {additionalOwnerCount > 0
          ? ` +${additionalOwnerCount} ${additionalOwnerCount === 1 ? "other" : "others"}`
          : ""}
      </Typography>
    </Stack>
  );
}

const noop = async () => {};

/**
 * A submission inside an archived folder. Archiving freezes the collection into a
 * snapshot, so nothing here is read from the live collection APIs — but it is laid out
 * like any other collection: the same Bookmarks / Works Cited / Notes tabs, read-only.
 */
export default function SubmissionSnapshotPage() {
  const { snapshotId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const { user } = useSelector((state) => state.auth);
  const columnStorageKey = `sooth-snapshot-columnvisibility-${user?.userId}`;
  const [storedVisibilityModel, setVisibilityModel] = useLocalStorage(
    columnStorageKey,
    SNAPSHOT_VISIBILITY_MODEL
  );
  const visibilityModel = { ...SNAPSHOT_VISIBILITY_MODEL, ...storedVisibilityModel };
  const handleVisibilityChange = (newVisibilityModel) => {
    const updated = { ...visibilityModel, ...newVisibilityModel };
    setVisibilityModel(updated);
    writeStorage(columnStorageKey, updated);
  };
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedTab, setSelectedTab] = useState("bookmarks");
  const [selectedSource, setSelectedSource] = useState(null);
  const [sidebarOption, setSidebarOption] = useState("citation");
  const [accessPopup, setAccessPopup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setSnapshot(await getArchiveSnapshotDetail(Number(snapshotId)));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [snapshotId]);

  useEffect(() => { load(); }, [load]);

  const header = snapshot?.header || null;
  const bookmarkRows = useMemo(() => mapSnapshotSources(snapshot), [snapshot]);
  const workCitedRows = useMemo(() => mapSnapshotWorkCited(snapshot), [snapshot]);

  const folderId = header?.submissionFolderID_lng;

  // The snapshot already carries the frozen share list, so Manage Access here is
  // read-only display only — no live share/remove/publish calls against archived data.
  const accessSidebarCollection = useMemo(
    () => ({
      collectionName_chr: header?.collectionName_chr,
      isShared_ysn: Boolean(header?.isShared_ysn),
      isPublic_ysn: false,
      isSubmitted_ysn: false,
    }),
    [header]
  );

  const getSharedUsers = useCallback(async () => {
    const sharedUsers = snapshot?.sharedUsers || [];
    return sharedUsers
      .filter((entry) => !entry.isCreator_ysn)
      .map((entry) => ({
        userId: entry.userID_chr,
        email: entry.userEmail_chr,
        userName: entry.userEmail_chr,
        sharedWithTypeId: entry.distributionListID_lng ? 2 : 1,
        isDistributionList: Boolean(entry.distributionListID_lng),
        generatedDistributionListName: entry.generatedDistributionName_chr,
      }));
  }, [snapshot]);

  // Same shape as the folder-list Owner column: creator first, then everyone else the
  // frozen share list carries, deduped by email.
  const owners = useMemo(() => {
    const sharedUsers = snapshot?.sharedUsers || [];
    const creator = sharedUsers.find((entry) => entry.isCreator_ysn);
    const primary = {
      email: creator?.userEmail_chr || header?.studentEmail_chr,
      userId: creator?.userID_chr || header?.studentUserID_chr,
      avatarUrl: null,
    };
    const others = sharedUsers
      .filter((entry) => !entry.isCreator_ysn)
      .map((entry) => ({
        email: entry.userEmail_chr,
        userId: entry.userID_chr,
        avatarUrl: null,
      }));
    const seenEmails = new Set();
    return [primary, ...others].filter((owner) => {
      if (!owner.email || seenEmails.has(owner.email)) return false;
      seenEmails.add(owner.email);
      return true;
    });
  }, [snapshot, header]);

  const tabSx = (key) => ({
    fontStyle: "italic",
    cursor: "pointer",
    textDecoration: selectedTab === key ? "underline" : "none",
    textDecorationThickness: "2px",
    textUnderlineOffset: "8px",
    color:
      selectedTab === key ? theme.palette.primary.main : theme.palette.text.primary,
  });

  // The same grid, columns, and column-visibility controls a collection uses; only the
  // sidebar handler differs, because snapshot rows must not hit the live source APIs.
  const bookmarkColumns = useColumns({
    fields: SNAPSHOT_COLUMNS,
    persistedWidths: {},
    commonWidth: null,
    theme,
    handlers: {
      handleSidebarOpen: (row, option) => {
        setSidebarOption(
          ["notes", "citation", "readinglevel"].includes(option) ? option : "citation"
        );
        setSelectedSource(row);
      },
    },
  });

  const renderBookmarks = () => (
    <Box>
      <ColumnsSection
        visibilityModel={visibilityModel}
        setVisibilityModel={setVisibilityModel}
        columns={bookmarkColumns}
        label={columnStorageKey}
      />
      <CollectionTableComponent
        apiRef={apiRef}
        rows={bookmarkRows}
        columns={bookmarkColumns}
        handleColumnResize={() => {}}
        visibilityModel={visibilityModel}
        handleVisibilityChange={handleVisibilityChange}
        getRowId={(row) => row.collectionSourceID_ids}
        showCustomEmptyMessage
        loading={loading}
        noRowsMessage="This snapshot contains no bookmarks."
      />
    </Box>
  );

  const downloadWorkCited = async (workCitedId) => {
    const entry = workCitedRows.find((row) => row.workCitedId_ids === workCitedId);
    try {
      const blob = await getArchiveWorkCitedDocument(workCitedId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${entry?.name_chr || "works-cited"}.docx`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);
    } catch (error) {
      console.error("Error downloading document:", error);
    }
  };

  const renderWorkCited = () => (
    <WorkCitedTable
      rows={workCitedRows}
      getWorkCitedDocument={downloadWorkCited}
      setWorkCitedName={() => {}}
      setWorkCitedFormatStyle={() => {}}
      setDeleteId={() => {}}
      setDeletePopup={() => {}}
      readOnly
      loading={loading}
    />
  );

  const renderTab = () => {
    switch (selectedTab) {
      case "workcited":
        return renderWorkCited();
      case "collectionnotes":
        return (
          <CollectionNotes
            markdownSummary={header?.collectionNote_chr || ""}
            setMarkdownSummary={() => {}}
            saveCollectionFolder={noop}
            handleCollectionOverview={noop}
            readOnly
            placeholder="No collection notes were saved with this submission."
          />
        );
      case "teachernotes":
        return (
          <CollectionNotes
            markdownSummary={header?.teacherNote_chr || ""}
            setMarkdownSummary={() => {}}
            saveCollectionFolder={noop}
            handleCollectionOverview={noop}
            readOnly
            placeholder="No teacher's notes were saved with this submission."
          />
        );
      default:
        return renderBookmarks();
    }
  };

  if (loadError && !snapshot) {
    return (
      <Box
        sx={{
          ...fillParent,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 1,
          p: 4,
        }}
      >
        <Typography variant="h6">Archived submission not found</Typography>
        <Typography variant="body1" color="text.secondary">
          This archived submission no longer exists, or you don't have access to it.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...fillParent, ...scrollingBox, px: { xs: 2, md: 4 } }}>
      <Helmet>
        <title>
          {`Sooth - Archived Submission${header?.collectionName_chr ? ` - ${header.collectionName_chr}` : ""}`}
        </title>
      </Helmet>
      <Stack gap={3} pb={8}>
        <Stack direction="row" gap={2} alignItems="center" sx={{ mt: 3, minWidth: 0 }}>
          <AccessibleIconButton
            title="Back to submission folder"
            onClick={() =>
              navigate(
                folderId
                  ? submissionFolderPath({
                      // A snapshot only exists for an archived folder.
                      submissionFolderID_ids: folderId,
                      isArchived_ysn: true,
                    })
                  : "/collections/submissions"
              )
            }
          >
            <ArrowBackIcon />
          </AccessibleIconButton>
          {loading && !header ? (
            <Skeleton variant="text" width="30rem" height={48} />
          ) : (
            <Typography
              color="#5c6d7e"
              variant="h3"
              fontSize="3rem"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: { xs: "2.25rem", sm: "3.5rem" },
                maxHeight: "7rem",
                flexShrink: 1,
              }}
            >
              {header?.collectionName_chr || "Archived submission"}
            </Typography>
          )}
          {!loading && (
            <AccessibleIconButton title="Submitted" sx={{ p: 0, height: 18, width: 18 }}>
              <TaskOutlinedIcon color="primary" />
            </AccessibleIconButton>
          )}
          {header?.isShared_ysn && (
            <AccessibleIconButton
              title="Sharing"
              onClick={() => setAccessPopup(true)}
            >
              <PeopleOutlineIcon />
            </AccessibleIconButton>
          )}
        </Stack>

        <Alert severity="info">
          This is a read-only snapshot taken when the folder was archived. It does not
          change if the student edits their collection.
        </Alert>

        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <StatTile label="Owner" loading={loading}>
            <SnapshotOwners owners={owners} />
          </StatTile>
          <StatTile
            label="Submitted"
            loading={loading}
            value={getRegularDate(header?.submittedDateTime_dtm)}
          />
          <StatTile
            label="Archived"
            loading={loading}
            value={getRegularDate(header?.archivedDateTime_dtm)}
          />
          <StatTile
            label="Sources"
            loading={loading}
            value={header?.sourceCount_int ?? bookmarkRows.length}
          />
          <StatTile
            label="Reviewed"
            loading={loading}
            value={header?.isReviewed_ysn ? "Yes" : "No"}
          />
        </Stack>

        <Stack direction="row" gap={3} flexWrap="wrap" sx={{ mb: "8px" }}>
          {TABS.map((tab) => (
            <Typography
              key={tab.key}
              variant="h5"
              sx={tabSx(tab.key)}
              onClick={() => setSelectedTab(tab.key)}
            >
              {tab.label}
            </Typography>
          ))}
        </Stack>

        {renderTab()}
      </Stack>
      <Footer sx={{ px: 0 }} />

      {/* The bookmark sidebar of an archived collection: everything is visible, nothing
          can be edited, so there are no summary, citation-style, or note controls. */}
      <RightDrawer open={Boolean(selectedSource)} onClose={() => setSelectedSource(null)}>
        <SnapshotBookmarkSidebar
          source={selectedSource}
          option={sidebarOption}
          onClose={() => setSelectedSource(null)}
        />
      </RightDrawer>

      {accessPopup && (
        <AccessSidebar
          accessPopup={accessPopup}
          setAccessPopup={setAccessPopup}
          collection={accessSidebarCollection}
          getUsersList={getSharedUsers}
          shareCollectionFolder={noop}
          removeSharedUser={noop}
          makeCollectionPublic={noop}
          handleRemoveAllSharedUsers={noop}
          length={bookmarkRows.length}
          readOnly
        />
      )}
    </Box>
  );
}
